import { create } from "zustand";
import type { ActiveFilter } from "@/components/ui/filter-panel";
import type { ListJobsSortBy } from "@/lib/api/model/listJobsSortBy";

// ── Wizard state ────────────────────────────────────────────────

export interface WizardStageItem {
  id: string;
  name: string;
  color: string;
  description: string;
  is_system: boolean;
  /** Original ATS stage name when imported — shown when the Arbi label diverges. */
  ats_name?: string;
}

export type WizardUploadState = "uploading" | "uploaded" | "failed";

export interface WizardUploadItem {
  id: string;
  file: File;
  state: WizardUploadState;
  progress: number;
  error?: string;
  controller?: AbortController;
  profileImportId?: string;
}

export interface WizardFormData {
  sourcingType: "scratch" | "ats_import";
  title: string;
  description: string;
  managerId: string | null;
  employerId: string | null;
  stages: WizardStageItem[];
  uploadedFiles: WizardUploadItem[];
  importUrls: string[];
  importStageId: string | null;
  // ats_import: which connection + remote job to import from.
  connectionId: string | null;
  remoteJobId: string | null;
}

const DEFAULT_STAGES: WizardStageItem[] = [
  { id: "sourced", name: "Sourced", color: "#2B5DF3", description: "", is_system: true },
  { id: "inbox", name: "Inbox", color: "#63D47A", description: "", is_system: false },
  { id: "archived", name: "Archived", color: "#F84043", description: "", is_system: true },
];

const INITIAL_FORM: WizardFormData = {
  sourcingType: "scratch",
  title: "",
  description: "",
  managerId: null,
  employerId: null,
  stages: DEFAULT_STAGES,
  uploadedFiles: [],
  importUrls: [],
  importStageId: null,
  connectionId: null,
  remoteJobId: null,
};

export type WizardMode = "create" | "clone";

export interface JobWizardClonePrefill {
  sourceId: string;
  sourceName: string;
  form: WizardFormData;
  /** Project the source job belongs to; pre-selected as the clone destination. */
  projectId?: string | null;
}

interface WizardState {
  open: boolean;
  mode: WizardMode;
  /** Source job id when `mode === "clone"`; null otherwise. */
  cloneSourceId: string | null;
  /** Source job title for the clone banner. */
  cloneSourceName: string | null;
  /** Destination project for the clone; null outside clone mode. */
  cloneTargetProjectId: string | null;
  step: 1 | 2 | 3 | 4;
  maxStepReached: 1 | 2 | 3 | 4;
  form: WizardFormData;
  createdJobId: string | null;
  createdJobProjectId: string | null;

  openWizard: () => void;
  openClone: (prefill: JobWizardClonePrefill) => void;
  closeWizard: () => void;
  resumeWizard: (
    jobId: string,
    step: number,
    form: Partial<WizardFormData>,
    projectId?: string | null,
  ) => void;
  setStep: (step: 1 | 2 | 3 | 4) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateForm: <K extends keyof WizardFormData>(key: K, value: WizardFormData[K]) => void;
  setCloneTargetProjectId: (projectId: string | null) => void;
  setCreatedJobId: (id: string, projectId?: string | null) => void;
  resetWizard: () => void;
}

export const useJobWizardStore = create<WizardState>((set) => ({
  open: false,
  mode: "create",
  cloneSourceId: null,
  cloneSourceName: null,
  cloneTargetProjectId: null,
  step: 1,
  maxStepReached: 1,
  form: { ...INITIAL_FORM, stages: INITIAL_FORM.stages.map((s) => ({ ...s })) },
  createdJobId: null,
  createdJobProjectId: null,

  openWizard: () =>
    set({
      open: true,
      mode: "create",
      cloneSourceId: null,
      cloneSourceName: null,
      cloneTargetProjectId: null,
      step: 1,
      maxStepReached: 1,
      form: { ...INITIAL_FORM, stages: INITIAL_FORM.stages.map((s) => ({ ...s })) },
      createdJobId: null,
      createdJobProjectId: null,
    }),
  openClone: ({ sourceId, sourceName, form, projectId }) =>
    set({
      open: true,
      mode: "clone",
      cloneSourceId: sourceId,
      cloneSourceName: sourceName,
      cloneTargetProjectId: projectId ?? null,
      step: 1,
      maxStepReached: 1,
      form: {
        ...INITIAL_FORM,
        ...form,
        stages: form.stages.map((s) => ({ ...s })),
        uploadedFiles: [],
        importUrls: [],
      },
      createdJobId: null,
      createdJobProjectId: null,
    }),
  closeWizard: () =>
    set({
      open: false,
      mode: "create",
      cloneSourceId: null,
      cloneSourceName: null,
      cloneTargetProjectId: null,
    }),
  resumeWizard: (jobId, step, formData, projectId) => {
    const clamped = Math.min(Math.max(step, 1), 4) as 1 | 2 | 3 | 4;
    set({
      open: true,
      mode: "create",
      cloneSourceId: null,
      cloneSourceName: null,
      cloneTargetProjectId: null,
      step: clamped,
      maxStepReached: clamped,
      form: {
        ...INITIAL_FORM,
        stages: INITIAL_FORM.stages.map((s) => ({ ...s })),
        ...formData,
      },
      createdJobId: jobId,
      createdJobProjectId: projectId ?? null,
    });
  },
  setStep: (step) => set({ step }),
  nextStep: () =>
    set((s) => {
      const next = Math.min(s.step + 1, 4) as 1 | 2 | 3 | 4;
      return { step: next, maxStepReached: Math.max(s.maxStepReached, next) as 1 | 2 | 3 | 4 };
    }),
  prevStep: () => set((s) => ({ step: Math.max(s.step - 1, 1) as 1 | 2 | 3 | 4 })),
  updateForm: (key, value) =>
    set((s) => ({ form: { ...s.form, [key]: value } })),
  setCloneTargetProjectId: (projectId) =>
    set({ cloneTargetProjectId: projectId }),
  setCreatedJobId: (id, projectId) =>
    set({ createdJobId: id, createdJobProjectId: projectId ?? null }),
  resetWizard: () =>
    set({
      open: false,
      mode: "create",
      cloneSourceId: null,
      cloneSourceName: null,
      cloneTargetProjectId: null,
      step: 1,
      maxStepReached: 1,
      form: { ...INITIAL_FORM, stages: INITIAL_FORM.stages.map((s) => ({ ...s })) },
      createdJobId: null,
      createdJobProjectId: null,
    }),
}));

// ── Job List state ──────────────────────────────────────────────

interface JobListState {
  search: string;
  listFilters: ActiveFilter[];
  page: number;
  perPage: number;
  sortBy: ListJobsSortBy;
  sortDir: "asc" | "desc";

  setSearch: (search: string) => void;
  setListFilters: (filters: ActiveFilter[]) => void;
  setPage: (page: number) => void;
  setSortBy: (field: ListJobsSortBy) => void;
  toggleSortDir: () => void;
  resetFilters: () => void;
}

export const useJobListStore = create<JobListState>((set) => ({
  search: "",
  listFilters: [],
  page: 1,
  perPage: 15,
  sortBy: "created_at",
  sortDir: "desc",

  setSearch: (search) => set({ search, page: 1 }),
  setListFilters: (listFilters) => set({ listFilters, page: 1 }),
  setPage: (page) => set({ page }),
  setSortBy: (field) =>
    set((s) => ({
      sortBy: field,
      sortDir: s.sortBy === field && s.sortDir === "asc" ? "desc" : "asc",
      page: 1,
    })),
  toggleSortDir: () => set((s) => ({ sortDir: s.sortDir === "asc" ? "desc" : "asc" })),
  resetFilters: () => set({ search: "", listFilters: [], page: 1 }),
}));
